import { useMemo } from 'react';
import { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { LumeoFileWithSearchMeta } from 'types/file_with_search_meta';

import { useFile } from './useFile';
import { useFilesSearch } from './useFilesSearch';

type UseFileWithSearchMetadataParams = {
  include_search_metadata?: boolean | undefined;
};

type UseFileWithSearchMetadataOptions = UseQueryOptions<
  LumeoFileWithSearchMeta,
  APIError
> & {
  params?: UseFileWithSearchMetadataParams;
};

/**
 * Hook that fetches a single file and optionally enriches it with search metadata
 * (events, objects, texts) when include_search_metadata is true
 */
export function useFileWithSearchMetadata(
  id: string | null | undefined,
  { params, ...options }: UseFileWithSearchMetadataOptions = {}
) {
  const includeSearchMetadata = params?.include_search_metadata === true;

  // Fetch file with the base useFile hook
  const fileQuery = useFile(id, {
    ...options,
  });

  // Only fetch search metadata if requested and file is loaded
  const shouldFetchSearch =
    includeSearchMetadata && fileQuery.isSuccess && fileQuery.data;

  // Fetch search metadata for the single file
  const searchQuery = useFilesSearch({
    params: {
      file_ids: fileQuery.data ? [fileQuery.data.id] : [],
      limit: 1, // We only need one result
    },
    enabled: shouldFetchSearch && Boolean(fileQuery.data?.id),
    queryKey: ['file-search-metadata', fileQuery.data?.id],
  });

  // Merge search metadata into file
  const enrichedData = useMemo(() => {
    if (!includeSearchMetadata || !fileQuery.data) {
      return fileQuery.data;
    }

    if (!searchQuery.data?.data?.[0]) {
      return fileQuery.data;
    }

    const searchResult = searchQuery.data.data[0];

    // Enrich file with search metadata
    return {
      ...fileQuery.data,
      events: searchResult.events,
      objects: searchResult.objects,
      texts: searchResult.texts,
      trackables: searchResult.trackables,
    };
  }, [includeSearchMetadata, fileQuery.data, searchQuery.data]);

  const isSearchMetadataLoading =
    includeSearchMetadata && searchQuery.isLoading;
  const isSearchMetadataFetching =
    includeSearchMetadata && searchQuery.isFetching;

  // Return the file query with enriched data
  // Search metadata should not block rendering the base file data
  return {
    ...fileQuery,
    data: enrichedData,
    isSearchMetadataLoading,
    isLoading: fileQuery.isLoading,
    isFetching: fileQuery.isFetching || isSearchMetadataFetching,
  } as UseQueryResult<LumeoFileWithSearchMeta, APIError> & {
    isSearchMetadataLoading: boolean;
  };
}
